import type { ReactNode } from 'react';

import { useParams } from 'react-router';

import env from '~/config';
import { urls } from '~/urls';

import { brandInfo } from '#/brand';
import { channels } from '#/channels';

const ContactBlock = ({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) => {
  return (
    <div className="flex h-full items-center justify-center p-8 odd:bg-primary-2-20 even:bg-primary-2-10">
      <div className="flex flex-col gap-y-6">
        <div className="flex flex-col gap-y-4">
          <h2 className="text-center font-heading-condensed text-xl font-medium leading-7 tracking-tight text-primary-2-80">
            {title}
          </h2>
          <p className="text-center leading-5 tracking-tight text-neutral-2">{description}</p>
        </div>
        <div className="text-center font-heading-condensed text-lg font-medium leading-6 tracking-tight text-secondary-1">
          {children}
        </div>
      </div>
    </div>
  );
};

const urlGmaps = ({
  address,
  key,
  size,
  zoom = '16',
  scale = '1',
}: {
  address: string;
  key: string;
  size: string;
  zoom?: string;
  scale?: string;
}) => {
  const qs = new URLSearchParams({
    center: address,
    markers: `${[].join('|')}|${address}`,
    zoom,
    size,
    key,
    scale,
  });

  const url = `${urls.staticMap()}?${qs.toString()}`;

  return url;
};

export default function Page() {
  const params = useParams();
  const channel = channels[params.channel as keyof typeof channels];
  const entity = channel?.entity;
  const instagramSocial = brandInfo.socials?.find((s) => s.name === 'Instagram');
  const contactEmail = brandInfo.emails.contact;
  const phone = channel?.urlSlug === 'in' ? brandInfo.phone.india : brandInfo.phone.uae;
  const store = entity?.store ?? brandInfo.store;

  return (
    <main className="w-full py-10">
      <div className="mb-10 flex items-center justify-center">
        <h1 className="font-heading-condensed text-xl font-medium leading-7 tracking-tight text-primary-2-80">
          Contact Us
        </h1>
      </div>
      <div className="lg:grid lg:grid-cols-2">
        <div className="grid grid-rows-5">
          <ContactBlock
            title="Come say hello!"
            description="We'd love to hear from you. If you've got questions, feedback or even just want to say hi. Email us."
          >
            <a href={`mailto:${contactEmail}`} rel="noreferrer" target="_blank">
              {contactEmail}
            </a>
          </ContactBlock>
          <ContactBlock
            title="In a hurry?"
            description="If you need assistance with an order, let us know."
          >
            <a
              rel="noreferrer"
              target="_blank"
              href={`https://wa.me/${phone.replaceAll('+', '').replaceAll(' ', '')}`}
            >
              {phone}
            </a>
          </ContactBlock>
          <ContactBlock
            title="Want to partner?"
            description="Looking for a partnership or interested in our products?"
          >
            <a href={`mailto:${brandInfo.emails.sales}`} rel="noreferrer" target="_blank">
              {brandInfo.emails.sales}
            </a>
          </ContactBlock>
          {instagramSocial && (
            <ContactBlock
              title="Join the ride"
              description="Follow us on Instagram and see what we're upto!"
            >
              <a href={instagramSocial.url} rel="noreferrer" target="_blank">
                @{new URL(instagramSocial.url).pathname.replace(/^\/|\/$/g, '')}
              </a>
            </ContactBlock>
          )}
        </div>
        {store && (
          <div className="mt-10 lg:mt-0 lg:grid lg:grid-rows-5">
            <a
              href={store.gmaps}
              rel="noreferrer"
              target="_blank"
              className="h-full w-full lg:row-span-4"
            >
              <picture>
                <source
                  srcSet={urlGmaps({
                    address: store.address,
                    key: env.PUBLIC_GOOGLE_KEY,
                    size: '313x500',
                    zoom: '18',
                    scale: '2',
                  })}
                  media={`(min-width: 1024px)`}
                />
                <source
                  srcSet={urlGmaps({
                    address: store.address,
                    key: env.PUBLIC_GOOGLE_KEY,
                    size: '500x200',
                    zoom: '18',
                    scale: '2',
                  })}
                  media={`(min-width: 768px)`}
                />
                <source
                  srcSet={urlGmaps({
                    address: store.address,
                    key: env.PUBLIC_GOOGLE_KEY,
                    size: '320x200',
                    zoom: '18',
                    scale: '2',
                  })}
                  media={`(min-width: 640px)`}
                />
                <img
                  className="h-full w-full object-cover"
                  src={urlGmaps({
                    address: store.address,
                    key: env.PUBLIC_GOOGLE_KEY,
                    size: '320x200',
                    zoom: '18',
                    scale: '2',
                  })}
                  alt={`${entity?.name ?? brandInfo.name} address map`}
                />
              </picture>
            </a>
            <ContactBlock
              title="Come meet us!"
              description="In the area and want to say hi? Drop by our store!"
            >
              <a href={store.gmaps} rel="noreferrer" target="_blank">
                {store.address}
              </a>
            </ContactBlock>
          </div>
        )}
      </div>
    </main>
  );
}
